import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  MessageCircle,
  Calendar,
  Hash,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  Globe,
  CreditCard,
} from "lucide-react";
import { GetSingleClientThunk } from "../../redux/states/clients/thunk";

const ViewClients = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleClient, loading, error } = useSelector(
    (state) => state.clients
  );

  useEffect(() => {
    dispatch(GetSingleClientThunk(uuid));
  }, [dispatch, uuid]);

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading client details...
            </p>
            <p className="text-gray-500 mt-2">Please wait</p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!singleClient) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
          <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Client Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              The client you are looking for is not available.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto transition-all duration-300 transform hover:scale-105"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Client List
            </button>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
        };
      case "inactive":
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertCircle,
        };
    }
  };

  const StatusBadge = getStatusBadge(singleClient.status);
  const StatusIcon = StatusBadge.icon;

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-12xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-4 lg:mb-0">
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {singleClient.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-gray-800">
                        {singleClient.profile_data?.name || singleClient.name}
                      </h1>
                      <p className="text-gray-600 flex items-center gap-2 mt-1">
                        <User className="w-4 h-4" />
                        Username: {singleClient.name}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold border flex items-center gap-1 ${StatusBadge.color}`}
                  >
                    <StatusIcon className="w-4 h-4" />
                    {singleClient.status}
                  </span>
                </div>
                <p className="text-gray-600 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  UUID: {singleClient.uuid}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold border flex items-center gap-1 ${
                    singleClient.role === "customer"
                      ? "bg-purple-100 text-purple-800 border-purple-200"
                      : "bg-blue-100 text-blue-800 border-blue-200"
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  {singleClient.role}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Profile Image and Basic Info */}
            <div className="lg:col-span-1 space-y-6">
              {/* Profile Image */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Profile Image
                </h2>
                {singleClient.profile_data?.profile_image ? (
                  <div className="relative">
                    <img
                      src={`${process.env.REACT_APP_API_URL}/${singleClient.profile_data.profile_image}`}
                      alt={singleClient.profile_data.name}
                      className="w-full h-64 object-cover rounded-xl border-2 border-gray-200 shadow-sm"
                    />
                  </div>
                ) : (
                  <div className="w-full h-64 flex flex-col items-center justify-center bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 text-gray-400">
                    <User className="w-16 h-16 mb-2" />
                    <p>No Profile Image</p>
                  </div>
                )}
              </div>

              {/* Account Stats */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Account Information
                </h2>
                <div className="space-y-3">
                  <StatCard
                    label="User ID"
                    value={singleClient.id}
                    color="blue"
                  />
                  <StatCard
                    label="Profile ID"
                    value={singleClient.profile_data?.id || "N/A"}
                    color="green"
                  />
                  <StatCard
                    label="Role"
                    value={singleClient.role}
                    color="purple"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-green-600" />
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoCard
                    icon={Mail}
                    label="Primary Email"
                    value={singleClient.email}
                    className="text-green-600 bg-green-50 border-green-200"
                  />
                  <InfoCard
                    icon={Mail}
                    label="Profile Email"
                    value={singleClient.profile_data?.email || "Not set"}
                    className="text-blue-600 bg-blue-50 border-blue-200"
                  />
                  <InfoCard
                    icon={Phone}
                    label="Mobile Number"
                    value={singleClient.profile_data?.mobile || "Not set"}
                    sublabel="Primary contact"
                  />
                  <InfoCard
                    icon={MessageCircle}
                    label="WhatsApp"
                    value={singleClient.profile_data?.whatsapp || "Not set"}
                    sublabel="Business contact"
                  />
                </div>
              </div>

              {/* Address Information */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-600" />
                  Address Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InfoCard
                    icon={MapPin}
                    label="Address"
                    value={singleClient.profile_data?.address || "Not set"}
                  />
                  <InfoCard
                    icon={Building}
                    label="City"
                    value={singleClient.profile_data?.city || "Not set"}
                  />
                  <InfoCard
                    icon={Globe}
                    label="State"
                    value={singleClient.profile_data?.state || "Not set"}
                  />
                  <InfoCard
                    icon={Globe}
                    label="Country"
                    value={singleClient.profile_data?.country || "Not set"}
                  />
                  <InfoCard
                    icon={Hash}
                    label="Pincode"
                    value={singleClient.profile_data?.pincode || "Not set"}
                  />
                  <InfoCard
                    icon={Building}
                    label="Company Name"
                    value={singleClient.profile_data?.company_name || "Not set"}
                  />
                </div>
              </div>

              {/* Account Details */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-orange-600" />
                  Account Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InfoCard
                    icon={Hash}
                    label="User UUID"
                    value={singleClient.uuid}
                    sublabel="Unique identifier"
                  />
                  <InfoCard
                    icon={Hash}
                    label="Profile UUID"
                    value={singleClient.profile_data?.uuid || "N/A"}
                    sublabel="Profile identifier"
                  />
                  <InfoCard
                    icon={Shield}
                    label="Account Status"
                    value={singleClient.status}
                    className={
                      singleClient.status === "active"
                        ? "text-green-600 bg-green-50 border-green-200"
                        : "text-red-600 bg-red-50 border-red-200"
                    }
                  />
                  <InfoCard
                    icon={Shield}
                    label="Profile Status"
                    value={singleClient.profile_data?.status || "N/A"}
                    className={
                      singleClient.profile_data?.status === "active"
                        ? "text-green-600 bg-green-50 border-green-200"
                        : "text-red-600 bg-red-50 border-red-200"
                    }
                  />
                  <InfoCard
                    icon={Calendar}
                    label="Account Created"
                    value={new Date(singleClient.created_at).toLocaleDateString(
                      "en-IN"
                    )}
                    sublabel={new Date(
                      singleClient.created_at
                    ).toLocaleTimeString("en-IN")}
                  />
                  <InfoCard
                    icon={Calendar}
                    label="Last Updated"
                    value={new Date(singleClient.updated_at).toLocaleDateString(
                      "en-IN"
                    )}
                    sublabel={new Date(
                      singleClient.updated_at
                    ).toLocaleTimeString("en-IN")}
                  />
                </div>
              </div>

              {/* Additional Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DescriptionCard
                  icon={CreditCard}
                  title="GST Information"
                  content={
                    singleClient.profile_data?.gst_number ||
                    "No GST number provided"
                  }
                />
                <DescriptionCard
                  icon={User}
                  title="Profile Summary"
                  content={`User ${
                    singleClient.profile_data?.name || singleClient.name
                  } is a ${singleClient.role} based in ${
                    singleClient.profile_data?.city || "unknown location"
                  }. Account is currently ${singleClient.status}.`}
                />
              </div>

              {/* System Information */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-gray-600" />
                  System Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoCard
                    label="Google ID"
                    value={singleClient.google_id || "Not connected"}
                  />
                  <InfoCard
                    label="OTP Status"
                    value={singleClient.otp ? "Set" : "Not set"}
                  />
                  <InfoCard
                    label="OTP Expires"
                    value={
                      singleClient.otp_expires_at
                        ? new Date(singleClient.otp_expires_at).toLocaleString(
                            "en-IN"
                          )
                        : "N/A"
                    }
                  />
                  <InfoCard
                    label="User ID Reference"
                    value={`User ID: ${
                      singleClient.profile_data?.user_id || "N/A"
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

/* -------------------- Helper Components -------------------- */
const InfoCard = ({ icon: Icon, label, value, sublabel, className = "" }) => (
  <div
    className={`border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300 ${className}`}
  >
    {Icon && (
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
    )}
    {!Icon && <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>}
    <p className="text-lg font-semibold text-gray-900 truncate">
      {value || "—"}
    </p>
    {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
  </div>
);

const StatCard = ({ label, value, color }) => (
  <div
    className={`flex justify-between items-center p-3 bg-${color}-50 rounded-lg border border-${color}-200`}
  >
    <span className={`text-${color}-700 font-medium`}>{label}</span>
    <span className={`text-2xl font-bold text-${color}-700`}>{value}</span>
  </div>
);

const DescriptionCard = ({ icon: Icon, title, content }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
      <Icon className="w-4 h-4 text-blue-600" />
      {title}
    </h3>
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 min-h-32">
      <p className="text-gray-700 leading-relaxed">
        {content || (
          <span className="text-gray-400 italic">No information available</span>
        )}
      </p>
    </div>
  </div>
);

export default ViewClients;
